// HowToUse.js

import React from 'react';
import AppShell from '../../../components/layout/AppShell';
import { ArrowUpCircle, CheckCircle, Info } from 'lucide-react';

const Feature = ({ title, imageSrc, description, steps, benefits, index }) => (
  <div id={`feature-${index + 1}`} className="public-card public-card-body">
    <div className="mb-6">
      <h3 className="text-2xl font-bold tracking-tight text-darkGrey sm:text-3xl">
        {index + 1}. {title}
      </h3>
    </div>
    <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
        <img
          src={imageSrc}
          alt={title}
          className="h-full w-full object-cover object-center"
        />
      </div>
      <div className="space-y-6">
        <p className="text-base leading-7 text-slateGrey">
          {description}
        </p>
        <div className="public-secondary-surface p-5 sm:p-6">
          <h4 className="mb-4 flex items-center text-lg font-semibold text-darkGrey">
            <CheckCircle className="mr-2 h-5 w-5 text-green-600" />
            How it works:
          </h4>
          <ul className="space-y-3">
            {steps.map((step, stepIndex) => (
              <li key={stepIndex} className="flex items-start">
                <span className="mr-2 mt-2 h-1.5 w-1.5 rounded-full bg-primaryBlue" />
                <span className="text-sm leading-6 text-slateGrey">{step}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="public-secondary-surface p-5 sm:p-6">
          <h4 className="mb-2 flex items-center text-lg font-semibold text-darkGrey">
            <Info className="mr-2 h-5 w-5 text-primaryBlue" />
            Benefits:
          </h4>
          <p className="text-sm leading-6 text-slateGrey">
            {benefits}
          </p>
        </div>
      </div>
    </div>
  </div>
);

const features = [
  {
    title: "Smart CV Customisation",
    imageSrc: "HowToUseImagery/smart-cv-customisation.png",
    description: "This feature tailors your CV for each job application by matching your skills and experiences to the employer's requirements. The image provided is an example, and actual results are customised specifically to each job description.",
    steps: [
      "Upload your current CV and the job description",
      "The system analyses the job advertisement and identifies key requirements",
      "It generates a checklist of essential qualifications",
      "Your CV is automatically adjusted to highlight relevant skills and experiences",
      "The system evaluates how well your CV aligns with the job requirements"
    ],
    benefits: "This feature saves time, improves performance with applicant tracking systems, and presents you as an ideal candidate for the role."
  },
  {
    title: "Bulk CV Conversion",
    imageSrc: "HowToUseImagery/BulkCVUpload.png",
    description: "This feature processes multiple CVs simultaneously, aligning them with a single job description:",
    steps: [
      "Upload up to 10 CVs and one job description",
      "Set conversion preferences (e.g., CV length, section limits)",
      "Receive tailored CVs aligned with the job requirements"
    ],
    benefits: "This feature streamlines the hiring process, ensures consistent formatting across all CVs, and quickly aligns multiple candidates to a specific role."
  },
  {
    title: "CV Content Control",
    imageSrc: "HowToUseImagery/cv-content-control.png",
    description: "This feature allows you to control the amount of information in different CV sections:",
    steps: [
      "Experience Limit: Select the number of work experiences to include",
      "Project Limit: Set the number of projects to showcase",
      "Skill Limit: Control the number of skills listed"
    ],
    benefits: "This feature keeps your CV concise, helps tailor it for specific jobs, and ensures your most impressive achievements are highlighted."
  },
  {
    title: "CV Length Optimisation",
    imageSrc: "HowToUseImagery/cv-length-optimisation.png",
    description: "This feature adjusts the level of detail in your CV with three options:",
    steps: [
      "Concise: Highly condensed, focusing on essential information",
      "Regular: Balanced output with comprehensive overview",
      "Detailed: Extensive version with in-depth information"
    ],
    benefits: "This feature helps tailor your CV's length to different job application requirements or industry standards."
  },
  {
    title: "Professional Tone Adjustment",
    imageSrc: "HowToUseImagery/professional-tone-adjustment.png",
    description: "This feature allows you to choose between first-person and third-person perspective:",
    steps: [
      "First Person to Third Person: Converts \"I\" to \"He/She/The candidate\"",
      "Maintain Third Person: Keeps all references in third person"
    ],
    benefits: "This feature ensures a uniform writing style, presenting a professional image across industries and application scenarios."
  },
  {
    title: "Personal Branding",
    imageSrc: "HowToUseImagery/personal-branding.png",
    description: "This feature adds a personal touch to your CV:",
    steps: [
      "Upload a small image (e.g., company logo) to appear in the top right corner",
      "The image is automatically included in your converted Word document CVs",
      "This is an optional feature - you can choose not to use it"
    ],
    benefits: "This simple addition makes your CV more memorable and professionally branded."
  }
];

export default function HowItWorks() {
  return (
    <AppShell currentPage="How-It-Works" loginRequired={false}>
      <main className="public-page">
        <div className="public-page-shell public-section">
          <div id="top" className="space-y-16">
            <div className="public-page-hero mb-0">
              <h1 className="public-page-title">How CVHQ Works</h1>
              <p className="public-page-intro">
                CVHQ offers powerful tools to create a polished, professional CV. This guide explains how to effectively use CVHQ to convert, customise, and optimise your CV, showcasing your unique skills and experiences.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/Scenarios"
                className="public-secondary-button"
              >
                Explore Scenarios
              </a>
            </div>

            <div className="public-secondary-surface p-5 sm:p-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {features.map((feature, index) => (
                  <a
                    key={feature.title}
                    href={`#feature-${index + 1}`}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-darkGrey transition-colors hover:border-primaryBlue hover:text-primaryBlue"
                  >
                    {feature.title}
                  </a>
                ))}
              </div>
            </div>

            <div className="space-y-8 sm:space-y-10">
              {features.map((feature, index) => (
                <Feature key={index} {...feature} index={index} />
              ))}
            </div>

            <div className="flex items-center justify-center">
              <a href="#top" className="public-link flex items-center">
                <ArrowUpCircle className="w-5 h-5 mr-2" />
                Back to top
              </a>
            </div>
          </div>
        </div>
      </main>
    </AppShell>
  );
}



